import { createFileRoute, Link } from "@tanstack/react-router";
import React from "react";
import { Award, Briefcase, Star, Target, Download, Share2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/shared/PageHeader";
import { GridSkeleton } from "@/components/shared/Skeletons";
import { apiUrl } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { useStudentCertificates, type StudentCertificate } from "@/hooks/useStudentData";

const badgeIcons: Record<string, React.ReactNode> = {
  award: <Award size={20} />,
  briefcase: <Briefcase size={20} />,
  star: <Star size={20} />,
  target: <Target size={20} />,
};

const designStyles: Record<string, { label: string }> = {
  completion: { label: "Completion" },
  participation: { label: "Participation" },
  excellence: { label: "Excellence" },
  achievement: { label: "Achievement" },
  placement: { label: "Placement" },
};

type Certificate = StudentCertificate;

/**
 * The public endpoints need no auth token, so the PDF and the share link are
 * plain URLs rather than authenticated fetches.
 */
const pdfUrl = (certNumber: string) =>
  apiUrl(`/public/certificates/verify/${encodeURIComponent(certNumber)}/pdf`);
const verifyUrl = (certNumber: string) =>
  apiUrl(`/public/certificates/verify/${encodeURIComponent(certNumber)}`);

/** evidence explains what earned the award, so a card never claims more than the record supports. */
function evidenceLine(cert: Certificate): string | null {
  if (cert.source_type === "placement" && cert.hours_earned) {
    return `${cert.hours_earned} verified hours`;
  }
  if (cert.source_type === "course" && typeof cert.score_percent === "number") {
    return `${cert.score_percent}% of lessons completed`;
  }
  return null;
}

export const Route = createFileRoute("/student/certificates/")({ component: StudentCertificates });

function CertificateCard({ cert }: { cert: Certificate }) {
  const styleKey =
    cert.type || (cert.source_type === "placement" ? "placement" : "completion");
  const style = designStyles[styleKey as keyof typeof designStyles] || designStyles.completion;
  const badgeEl = badgeIcons[cert.badge_icon as keyof typeof badgeIcons] || <Award size={20} />;
  const evidence = evidenceLine(cert);
  const certNumber = cert.cert_number || String(cert.id);

  const handleShare = async () => {
    const url = pdfUrl(certNumber);
    try {
      if (navigator.share) {
        await navigator.share({ title: "My Taskpart certificate", url });
        return;
      }
      await navigator.clipboard.writeText(url);
      toast.success("Certificate link copied");
    } catch {
      toast.error("Could not share this certificate");
    }
  };

  return (
    <article className="flex h-full flex-col overflow-hidden rounded-md border border-border bg-card transition-colors hover:border-hairline-strong">
      <div className="p-4">
        <div className="relative rounded-sm border border-border bg-background p-5 text-center">
          {/* Corner ornaments */}
          <span className="absolute top-1.5 left-1.5 size-3 border-t border-l border-hairline-strong" aria-hidden />
          <span className="absolute top-1.5 right-1.5 size-3 border-t border-r border-hairline-strong" aria-hidden />
          <span className="absolute bottom-1.5 left-1.5 size-3 border-b border-l border-hairline-strong" aria-hidden />
          <span className="absolute right-1.5 bottom-1.5 size-3 border-r border-b border-hairline-strong" aria-hidden />

          <span
            className="mx-auto mb-2 flex size-10 items-center justify-center rounded-full border border-border bg-card text-body [&_svg]:size-5"
            aria-hidden
          >
            {badgeEl}
          </span>
          <p className="eyebrow">Certificate of</p>
          <h3 className="mt-0.5 font-serif text-xl italic text-foreground">{style.label}</h3>

          <div className="my-2 flex items-center justify-center gap-1.5" aria-hidden>
            <span className="h-px w-6 bg-border" />
            <span className="size-1 rounded-full bg-muted-foreground" />
            <span className="h-px w-6 bg-border" />
          </div>

          <p className="text-[15px] font-medium text-foreground">
            {cert.student_name || "Recipient Name"}
          </p>
          {cert.course && <p className="mt-1 text-caption text-body italic">{cert.course}</p>}
          {cert.university && <p className="text-caption text-muted-foreground">{cert.university}</p>}
          {evidence && <p className="mt-1 text-caption text-muted-foreground italic">{evidence}</p>}

          <div className="mt-3 flex items-center justify-between gap-2 rounded-xs border border-border px-2 py-1 text-[11px] text-muted-foreground">
            <span>Issued: {cert.issue_date || "N/A"}</span>
            <span className="truncate font-mono">{cert.cert_number || `#${cert.id}`}</span>
          </div>

          <div className="mt-3 flex justify-center gap-6" aria-hidden>
            {["Authorised", "Director"].map((role) => (
              <div key={role} className="text-center">
                <span className="mx-auto mb-1 block h-px w-12 bg-hairline-strong" />
                <span className="text-[10px] tracking-wider text-muted-foreground uppercase">{role}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-auto flex justify-center gap-1 border-t border-border p-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => window.open(verifyUrl(certNumber), "_blank", "noopener")}
        >
          <ExternalLink />
          View
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => window.open(pdfUrl(certNumber), "_blank", "noopener")}
        >
          <Download />
          PDF
        </Button>
        <Button variant="ghost" size="sm" onClick={handleShare}>
          <Share2 />
          Share
        </Button>
      </div>
    </article>
  );
}

function StudentCertificates() {
  const { data: certificates = [], isLoading } = useStudentCertificates();

  if (isLoading) {
    return (
      <div>
        <PageHeader
          title="My certificates"
          subtitle="Your earned digital credentials and certificates."
          breadcrumbs={[
            { label: "Dashboard", path: "/student/dashboard" },
            { label: "My Certificates" },
          ]}
        />
        <GridSkeleton cards={6} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="My certificates"
        subtitle="Your earned digital credentials and verified certificates from training programmes."
        breadcrumbs={[
          { label: "Dashboard", path: "/student/dashboard" },
          { label: "My Certificates" },
        ]}
      />

      {certificates.length === 0 ? (
        <div className="flex flex-col items-center rounded-lg border border-dashed border-border bg-card px-6 py-12 text-center">
          <span
            className="mb-4 flex size-10 items-center justify-center rounded-md border border-border bg-background text-muted-foreground"
            aria-hidden
          >
            <Award className="size-5" />
          </span>
          <h2 className="text-base font-medium text-foreground">No certificates yet</h2>
          <p className="mt-1 max-w-md text-sm text-muted-foreground">
            Complete your field attachment or training programme to earn verified digital
            certificates.
          </p>
          <Button asChild className="mt-5">
            <Link to="/student/courses">Browse programmes</Link>
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {certificates.map((cert: Certificate) => (
            <CertificateCard key={cert.id} cert={cert} />
          ))}
        </div>
      )}
    </div>
  );
}
